import _uniqBy from 'lodash/uniqBy';
import capitalizeFirst from '@zola-helpers/client/dist/es/transformers/capitalizeFirst';
import { CARD_TYPE_MAP } from '@zola/zola-ui/src/paper/cards/constants/cardTypes';
import { isAllPaperShape } from 'cards/constants/CardLevelOptions';
import { isSeoColorType } from 'cards/constants/seoColors';
import { PARAM_KEY_TO__FILTER } from '../types';
import { FilterKey, FilterValue, ActiveFilters } from './CardListingFilter/FilterTypes';
import { PARAM_CATEGORIES } from './constants';

export function getQueryFromFilter(
  key: keyof typeof PARAM_KEY_TO__FILTER,
  value: FilterValue,
  query?: Record<string, string>,
  urlKey?: string
): string {
  const paramKey = PARAM_KEY_TO__FILTER[key] || '';
  const queryKey = urlKey || key;
  let newQuery;

  if (query && query[queryKey]) {
    newQuery = value
      ? `${query[queryKey]},${paramKey}`
      : query[queryKey]
          .split(',')
          .filter(e => e !== paramKey)
          .join(',');
  } else {
    newQuery = paramKey;
  }
  return newQuery;
}

export function getFilter(
  filters: string[],
  onUpdateSearchFilters: (filterName: FilterKey, isFiltering: boolean) => void
): void {
  filters.forEach(filter => {
    const property = Object.keys(PARAM_KEY_TO__FILTER).find(
      each => filter === PARAM_KEY_TO__FILTER[each]
    );
    if (property) {
      onUpdateSearchFilters(property as FilterKey, true);
    }
  });
}

export const removeElementWithUuidDuplicates = <T extends { uuid: string }>(array: T[]): T[] =>
  _uniqBy(array, 'uuid');

export const getMotifsFromUrl = (urlFilter: string): string[] => {
  if (!urlFilter) return [];
  const activeStyles = urlFilter.split('--');
  const activeMotifFilterStyles = activeStyles
    .filter(e => !isSeoColorType(e.toUpperCase()) && !isAllPaperShape(e))
    .map(e => capitalizeFirst(e));
  return activeMotifFilterStyles;
};

export const getSilhouettesFromUrl = (urlFilter: string): string[] => {
  if (!urlFilter) return [];

  const activeStyles = urlFilter.split('--');
  const activeSilhouettesFilterStyles = activeStyles.filter(isAllPaperShape).map(capitalizeFirst);
  return activeSilhouettesFilterStyles;
};

export const getSeoColorsFromUrl = (urlFilter: string): string[] => {
  if (!urlFilter) return [];
  const activeStyles = urlFilter.split('--');
  const activeSeoColorFilterStyles = activeStyles.map(e => e.toUpperCase()).filter(isSeoColorType);
  return activeSeoColorFilterStyles;
};

export const getFullSeoFiltersFromUrl = (urlFilter: string): string[] => {
  if (!urlFilter) return [];
  const activeStyles = urlFilter.split('--');
  const activeFilterStyles = activeStyles.map(e => e.toUpperCase());
  return activeFilterStyles;
};

export const getTitleCase = (text: string): string => {
  return text
    .split('_')
    .map((word: string) => word.slice(0, 1).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

export const getLabel = (color: string): string => {
  switch (color) {
    case 'BEIGE':
      return 'Off White';

    case 'MULTI':
      return 'Custom colors';

    default:
      return getTitleCase(color);
  }
};

const isMultipleFilterOptions = (filterName: FilterKey, activeStyles: FilterValue): boolean => {
  let isMultipleFilters = false;

  if (Array.isArray(activeStyles)) {
    if (filterName === 'motifs') {
      isMultipleFilters =
        activeStyles.filter(e => !isSeoColorType(e.toUpperCase()) && !isAllPaperShape(e)).length >
        1;
    } else if (filterName === 'silhouettes') {
      isMultipleFilters = activeStyles.filter(e => isAllPaperShape(e)).length > 1;
    } else {
      isMultipleFilters = activeStyles.filter(e => isSeoColorType(e.toUpperCase())).length > 1;
    }
  }
  return isMultipleFilters;
};

const getFilterName = (filterOption: string): FilterKey => {
  if (isAllPaperShape(filterOption)) {
    return 'silhouettes';
  }
  if (isSeoColorType(filterOption.toUpperCase())) {
    return 'multiColor';
  }

  return 'motifs';
};

export const getHeroNamedStyle = (activeStyles: FilterValue): string => {
  let filterTitle = '';
  if (Array.isArray(activeStyles)) {
    filterTitle =
      activeStyles.find(e => !isMultipleFilterOptions(getFilterName(e), activeStyles)) || '';
  }

  return getTitleCase(filterTitle);
};

export const existenceCheck = (value: FilterValue): boolean => {
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  return Boolean(value);
};

export const filterTransformation = (
  key: keyof typeof PARAM_KEY_TO__FILTER,
  value: FilterValue,
  query?: Record<string, string>,
  urlKey?: string
): string | null => {
  if (Array.isArray(value)) {
    return value.join(',').toLowerCase();
  }
  if (key === 'is_letterpress') {
    return value ? key : null;
  }

  return getQueryFromFilter(key, value, query, urlKey);
};

interface Filters
  extends Partial<Omit<ActiveFilters, 'seo_colors' | 'silhouettes'>>,
    Record<string, any> {
  digital_suite?: boolean;
  seo_colors?: string[] | string;
  silhouettes?: string[] | string;
}

export const getNewPathNameAndSearch = (
  newFilter: Filters,
  cardTypeUrl: string,
  isMultiSampleMode: boolean
): Record<string, string> => {
  const {
    motifs = [],
    silhouettes = [],
    seo_colors = [],
    digital_suite: isDigital,
    ...otherValues
  } = newFilter;

  //* BUILD PATHNAME USING MOTIF, SHAPES AND COLOR FILTERS
  const styleValues = [];

  styleValues.push(...motifs);
  styleValues.push(...(Array.isArray(seo_colors) ? seo_colors : [seo_colors]));
  styleValues.push(...(Array.isArray(silhouettes) ? silhouettes : [silhouettes]));

  const activeStylesLower = motifs.map(style => style.toLowerCase());
  const sortedLowercaseStyleValues = styleValues
    .map(e => e.toLowerCase())
    .sort((a, b) => {
      const index1 = activeStylesLower.indexOf(a);
      const index2 = activeStylesLower.indexOf(b);
      return (index1 > -1 ? index1 : Infinity) - (index2 > -1 ? index2 : Infinity);
    });

  const stylesString = styleValues.length ? `/${sortedLowercaseStyleValues.join('--')}` : '';
  const newPathname = `/wedding-planning/${
    isDigital ? 'digital/' : ''
  }${cardTypeUrl}/shop${stylesString}`;

  //* BUILD QUERY PARAMS USING ALL OTHER FILTERS
  // Go through all remaining filters, grouping all valid values by filter category.
  const filterValuesByCategory = {};
  Object.keys(otherValues).forEach(key => {
    // If the new filter value is falsy, it shouldn't be in the URL
    if (!otherValues[key]) {
      return;
    }

    // Only proceed if the filter is from a supported category
    const category = PARAM_CATEGORIES.find(c => c.keys.includes(key));
    if (category) {
      const value = filterTransformation(key, otherValues[key]);

      // Only proceed if the filter is set to a truthy value
      if (value) {
        const categoryKey = category.urlKey;
        // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre...
        const existingCategory = filterValuesByCategory[categoryKey];

        // Either add the value to an existing category or create a new one
        if (existingCategory) {
          existingCategory.push(value);
        } else {
          // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre...
          filterValuesByCategory[categoryKey] = [`${value}`];
        }
      }
    }
  });

  // Once all filters are split by category, stringify each individual category
  const searchValues = Object.keys(filterValuesByCategory).map(categoryKey => {
    // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre...
    const categoryValue = filterValuesByCategory[categoryKey].join(',');
    return `${categoryKey}=${categoryValue}`;
  });

  // Keep multi_sample_mode in the URL if currently enabled
  if (isMultiSampleMode) {
    searchValues.push('multi_sample_mode=multisampleenabled');
  }

  const newSearch = searchValues.length ? `?${searchValues.join('&')}` : '';

  return {
    newPathname,
    newSearch,
  };
};

export const getExpertAdviceCardsFromCardType = (cardType: string): string[] => {
  switch (cardType) {
    case CARD_TYPE_MAP.std:
      return [
        'when-to-send-save-the-dates',
        'a-guide-to-save-the-dates',
        'what-to-expect-after-sending-save-the-dates',
      ];
    case CARD_TYPE_MAP.invitation:
      return [
        'when-should-you-send-out-your-wedding-invitations',
        'the-best-ways-to-collect-wedding-rsvps',
        'wedding-invitations-size-guide',
        'how-to-stuff-wedding-invitations-a-complete-guide-to-creating-the-perfect',
        'pros-and-cons-of-printing-your-own-wedding-invitations',
        'pros-and-cons-of-digital-wedding-invitations',
        'how-to-word-wedding-invitations',
      ];
    case CARD_TYPE_MAP.details:
      return [
        'unique-enclosure-card-ideas',
        'wedding-enclosure-card-wording',
        'what-to-include-on-a-wedding-details-card',
      ];
    case CARD_TYPE_MAP.weddingShower:
      return [
        '10-creative-wedding-shower-invite-ideas',
        'when-to-send-out-bridal-shower-invites',
        'bridal-shower-invitation-wording',
      ];
    case CARD_TYPE_MAP.thankyou:
      return [
        'when-to-send-wedding-thank-you-cards',
        'thank-you-card-etiquette',
        'wedding-thank-you-card-wording',
      ];
    case CARD_TYPE_MAP.place:
      return [
        'a-guide-to-escort-cards',
        'what-to-write-on-wedding-place-cards',
        'do-you-need-place-cards-at-a-wedding',
      ];
    case CARD_TYPE_MAP.menu:
      return ['wedding-menu-card-ideas', 'a-guide-to-wedding-menu-cards', 'wedding-food-ideas'];
    case CARD_TYPE_MAP.program:
      return [
        'a-guide-to-wedding-ceremony-programs',
        'a-guide-to-the-wedding-ceremony-order-of-events',
        'wedding-day-timeline',
      ];
    default:
      return [];
  }
};
